import type { BrokerProduct } from '@/services/brokers/types';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

type QuoteParamsContextType = {
  policyId: string;
  proposalNumber: string;
  id: string;
  productId: string;
  category: BrokerProduct['category'];
  productCode: string;
  /**
   * Derivado do próprio contexto em vez de repetir os campos à mão: a lista
   * duplicada já tinha divergido (`category` era `string` aqui e a união de
   * `BrokerProduct['category']` acima).
   */
  setParams: (params: Partial<QuoteParamsValues>) => void;
};

type QuoteParamsValues = Omit<QuoteParamsContextType, 'setParams'>;

const QuoteParamsContext = createContext<QuoteParamsContextType | undefined>(
  undefined
);

export function QuoteParamsProvider({ children }: { children: ReactNode }) {
  // Tipado pelo contexto: sem isso o `useState` infere `category: string` e o
  // provider deixa de casar com a união de BrokerProduct['category'].
  const [params, setParamsState] = useState<QuoteParamsValues>({
    policyId: '',
    proposalNumber: '',
    id: '',
    productId: '',
    category: undefined,
    productCode: '',
  });

  const setParams = useCallback(
    (newParams: Partial<QuoteParamsValues>) => {
      setParamsState(prev => ({ ...prev, ...newParams }));
    },
    []
  );

  return (
    <QuoteParamsContext.Provider value={{ ...params, setParams }}>
      {children}
    </QuoteParamsContext.Provider>
  );
}

export function useQuoteParams() {
  const context = useContext(QuoteParamsContext);
  if (!context) {
    throw new Error('useQuoteParams must be used within QuoteParamsProvider');
  }
  return context;
}
